import { useState, type ReactNode } from "react";
import { Box, Divider, Fab, Popover, Stack, Tooltip, Typography, alpha, useTheme, type SxProps, type Theme } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import type { EspelhoCarga, TipoDia } from "@/api/escala.service";
import { COR_FERIADO, ROTULO_TIPO, TIPOS_LEGENDA, estiloTipo, fundoDaHora } from "@/components/escala/tiposDia";
import { botaoFlutuante, cantoAlterado, contornoSemHorario } from "@/components/escala/espelhoEstilos";
import { MarcaAlterado, SeloRevisar } from "@/components/escala/EspelhoLinha";
import { corDaJornada, formatarBloco } from "@/components/escala/cargaHoraria";

export const chipLegenda = { width: 24, height: 22, borderRadius: 1, display: "grid", placeItems: "center", fontSize: 11 } as const;

// Linha de legenda: o mesmo espaçamento em toda tela que tem cor.
export function LinhaLegenda({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={2.5} rowGap={1} aria-label="Legenda">
      {children}
    </Stack>
  );
}

export function TextoLegenda({ children }: { children: ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" fontWeight={500}>
      {children}
    </Typography>
  );
}

interface AmostraProps {
  cor: string;
  letra?: string;
  fg?: string;
  peso?: number;
  // Estilo a mais na amostra e o que vai dentro dela além da letra.
  sx?: SxProps<Theme>;
  marca?: ReactNode;
}

function Amostra({ cor, letra, fg, peso, sx, marca }: AmostraProps) {
  return (
    <Box sx={[{ ...chipLegenda, flexShrink: 0, bgcolor: cor, color: fg, fontWeight: peso }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {letra}
      {marca}
    </Box>
  );
}

type ItemLegendaProps = AmostraProps & { rotulo: string };

export function ItemLegenda({ rotulo, ...amostra }: ItemLegendaProps) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Amostra {...amostra} />
      <TextoLegenda>{rotulo}</TextoLegenda>
    </Stack>
  );
}

interface EspelhoLegendaProps {
  tipos?: readonly Exclude<TipoDia, "">[];
  feriado?: boolean;
}

// Legenda numa linha, para os relatórios que desenham dias.
export function EspelhoLegenda({ tipos = TIPOS_LEGENDA, feriado = true }: EspelhoLegendaProps = {}) {
  const theme = useTheme();
  return (
    <LinhaLegenda>
      {tipos.map((tipo) => {
        const { bg, fg, peso } = estiloTipo(theme, tipo);
        return <ItemLegenda key={tipo} cor={bg} fg={fg} peso={peso} letra={tipo} rotulo={ROTULO_TIPO[tipo]} />;
      })}
      {feriado && <ItemLegenda cor={COR_FERIADO} rotulo="Feriado" />}
    </LinhaLegenda>
  );
}

const EXPLICACAO_TIPO: Record<Exclude<TipoDia, "">, string> = {
  T: "Dia trabalhado.",
  F: "Folga.",
  FA: "Folga compensatória de um feriado trabalhado.",
  C: "Compensação.",
  A: "Afastamento ou férias cadastrados.",
  D: "Do desligamento em diante.",
};

function ItemExplicado({ rotulo, explicacao, ...amostra }: ItemLegendaProps & { explicacao: ReactNode }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Amostra {...amostra} />
      <Typography variant="body2" sx={{ lineHeight: "22px" }}>
        <strong>{rotulo}</strong> — {explicacao}
      </Typography>
    </Stack>
  );
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <Stack spacing={1}>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {titulo}
      </Typography>
      {children}
    </Stack>
  );
}

interface LegendaDoEspelhoProps {
  comCarga: boolean;
  editavel: boolean;
  // Cargas que aparecem na grade, na ordem em que entram na legenda.
  cargas?: EspelhoCarga[];
}

// Ícone "Legenda" do canto inferior direito do espelho: abre, acima dele, o que cada cor e cada
// marca da grade quer dizer, a cor de cada carga horária e os gestos de edição.
export function LegendaDoEspelho({ comCarga, editavel, cargas = [] }: LegendaDoEspelhoProps) {
  const theme = useTheme();
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  const t = estiloTipo(theme, "T");
  const fundoHora = fundoDaHora(theme);

  return (
    <>
      <Tooltip title="Legenda" placement="left">
        <Fab size="small" aria-label="Legenda" aria-expanded={!!ancora} sx={botaoFlutuante} onClick={(e) => setAncora(e.currentTarget)}>
          <InfoOutlined fontSize="small" />
        </Fab>
      </Tooltip>
      <Popover
        open={!!ancora}
        anchorEl={ancora}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: -1, p: 2, maxWidth: 440, maxHeight: "70vh", overflowY: "auto" } } }}
      >
        <Stack spacing={2} aria-label="Legenda">
          <Secao titulo="Tipos de dia">
            {TIPOS_LEGENDA.map((tipo) => {
              const { bg, fg, peso } = estiloTipo(theme, tipo);
              const explicacao =
                tipo === "T" && comCarga ? "Dia trabalhado. Mostra a hora de entrada, na cor da carga horária do dia." : EXPLICACAO_TIPO[tipo];
              return <ItemExplicado key={tipo} cor={bg} fg={fg} peso={peso} letra={tipo} rotulo={ROTULO_TIPO[tipo]} explicacao={explicacao} />;
            })}
          </Secao>

          <Divider />
          <Secao titulo="Marcas">
            <ItemExplicado cor={COR_FERIADO} rotulo="Feriado" explicacao="A coluna do feriado cadastrado no mês." />
            {comCarga && (
              <ItemExplicado
                cor={t.bg}
                fg={t.fg}
                peso={t.peso}
                letra="?"
                rotulo="Sem horário"
                sx={contornoSemHorario(t.fg)}
                explicacao="Dia trabalhado sem horário. Impede a validação. Passe o mouse no dia para ver o motivo: a carga não tem horário para esse tipo de dia, não é uma das cargas da escala, ou o colaborador não tem carga padrão."
              />
            )}
            <ItemExplicado
              cor={t.bg}
              fg={t.fg}
              peso={t.peso}
              letra="T"
              rotulo="Alterado à mão"
              sx={cantoAlterado}
              marca={<MarcaAlterado />}
              explicacao={comCarga ? "O tipo do dia ou a carga horária foram mudados à mão. O Resetar desfaz." : "O tipo do dia foi mudado à mão. O Resetar desfaz."}
            />
            <ItemExplicado
              cor={t.bg}
              fg={t.fg}
              peso={t.peso}
              letra="T"
              rotulo="Ocorrência"
              sx={{ outline: "2px solid", outlineColor: "warning.main", outlineOffset: 1 }}
              explicacao="O dia quebra uma regra da escala e impede a validação. Passe o mouse para ver qual."
            />
            <ItemExplicado
              cor={alpha(theme.palette.primary.main, 0.12)}
              fg={theme.palette.primary.main}
              peso={700}
              letra="AB"
              rotulo="Revisar"
              sx={{ position: "relative", borderRadius: "50%", width: 22 }}
              marca={<SeloRevisar erro />}
              explicacao="O colaborador tem ocorrência (vermelho) ou aviso (laranja). Passe o mouse no avatar para ver o quê e em que dias."
            />
          </Secao>

          {comCarga && cargas.length > 0 && (
            <>
              <Divider />
              <Secao titulo="Cargas horárias">
                {cargas.map((c) => (
                  <ItemExplicado
                    key={c.id}
                    cor={t.bg}
                    fg={t.fg}
                    peso={t.peso}
                    letra={c.util.entrada.slice(0, 2)}
                    rotulo={c.descricao}
                    sx={c.cor ? { color: corDaJornada(c.cor, fundoHora) } : undefined}
                    explicacao={c.cor ? `Dia útil ${formatarBloco(c.util)}.` : `Dia útil ${formatarBloco(c.util)}. Cor padrão.`}
                  />
                ))}
              </Secao>
            </>
          )}

          {editavel && (
            <>
              <Divider />
              <Secao titulo="Como editar">
                <Typography variant="body2">
                  <strong>Duplo clique</strong> no dia: alterna Trabalha → Folga → Compensação.
                </Typography>
                {comCarga && (
                  <Typography variant="body2">
                    <strong>Botão direito</strong> no dia trabalhado: trocar o horário do dia.
                  </Typography>
                )}
              </Secao>
            </>
          )}
        </Stack>
      </Popover>
    </>
  );
}
